import { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useForm, ValidationError } from '@formspree/react';
import { useSite } from '../context/SiteContext';
import Navbar from '../components/Navbar';
import HikerCharacter from '../components/HikerCharacter';
import TripCard from '../components/TripCard';
import WeatherWidget from '../components/WeatherWidget';
import Footer from '../components/Footer';
import SEO from '../components/SEO';
import { IconArrow, IconTree, IconInstagram, IconWhatsapp } from '../components/Icons';
import styles from './HomePage.module.css';

// ─── SECTION FADE-IN HOOK ─────────────────────────────────────────────────────
function useFadeIn(threshold = 0.15) {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { setVisible(true); observer.disconnect(); } },
      { threshold }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [threshold]);
  return [ref, visible];
}

export default function HomePage() {
  const { data } = useSite();
  const navigate = useNavigate();
  const location = useLocation();
  const [contactState, handleContactSubmit] = useForm('xzdydald');

  // Scroll to section if navigated here with state (e.g. from Corporate page)
  useEffect(() => {
    if (location.state?.scrollTo) {
      const id = location.state.scrollTo;
      setTimeout(() => {
        const el = document.getElementById(id);
        if (el) el.scrollIntoView({ behavior: 'smooth' });
      }, 150);
    }
  }, [location.state]);

  const [tripsRef, tripsVisible] = useFadeIn();
  const [aboutRef, aboutVisible] = useFadeIn();
  const [corpRef, corpVisible] = useFadeIn();
  const [blogRef, blogVisible] = useFadeIn();
  const [contactRef, contactVisible] = useFadeIn();
  const [reviewsRef, reviewsVisible] = useFadeIn();
  const heroRef = useRef(null);

  const thisWeekTrips = data.trips.filter(t => t.thisWeek && !t.softDeleted);

  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };



  return (
    <>
      <SEO
        title="Keepers Experience | Hiking Adventures in Jordan"
        description="Discover Jordan's most scenic hiking trails. Join Keepers Experience for weekly hikes, private trips, and corporate team building in the heart of Jordanian nature."
        image="https://keepersexperience.com/full_logo.png"
        url="https://keepersexperience.com"
        type="website"
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "LocalBusiness",
          "name": "Keepers Experience",
          "image": "https://keepersexperience.com/full_logo.png",
          "description": "Jordan's premier outdoor adventure company organizing weekly hikes, private trips, and corporate team-building experiences.",
          "url": "https://keepersexperience.com",
          "telephone": "+962776113530",
          "email": "experience@keepersoftheforest.com",
          "address": {
            "@type": "PostalAddress",
            "addressLocality": "Amman",
            "addressCountry": "JO",
            "addressRegion": "Amman"
          },
          "geo": {
            "@type": "GeoCoordinates",
            "latitude": "31.945",
            "longitude": "35.9284"
          },
          "sameAs": [
            "https://www.instagram.com/keepers.experience",
            "https://www.facebook.com/KeepersExperienceJordan"
          ],
          "priceRange": "25-200 JOD"
        }}
      />
      <div className={styles.page}>
      <Navbar />

      {/* ─── HERO ─── */}
      <section className={styles.hero} id="home" ref={heroRef}>
        <div className={styles.heroBg} />
        <div className={styles.heroParticles}>
          {[...Array(6)].map((_, i) => (
            <div key={i} className={styles.particle} style={{ '--i': i }} />
          ))}
        </div>
        <div className={styles.heroContent}>
          <div className={styles.heroLogo}>
            <img src="/white-logo.png" alt="Keepers Logo" />
          </div>
          <div className={styles.heroBadge}>{data.hero.badge}</div>
          <h1 className={styles.heroTitle}>
            {data.hero.headline.split("Jordan's").map((part, i) =>
              i === 0 ? part : <span key={i}><span className={styles.heroAccent}>Jordan's</span>{part}</span>
            )}
          </h1>
          <p className={styles.heroSub}>{data.hero.subheadline}</p>
          <div className={styles.heroBtns}>
            <button className={styles.btnPrimary} onClick={() => scrollTo('trips')}>
              {data.hero.cta} <IconArrow size={18} />
            </button>
            <button className={styles.btnSecondary} onClick={() => scrollTo('about')}>
              Our Story
            </button>
          </div>
          <div className={styles.heroStats}>
            {data.about.stats.slice(0, 3).map((s, i) => (
              <div key={i} className={styles.heroStat}>
                <span className={styles.heroStatVal}>{s.value}</span>
                <span className={styles.heroStatLabel}>{s.label}</span>
              </div>
            ))}
          </div>
        </div>
        <button className={styles.heroScroll} onClick={() => scrollTo('trips')}>
          <div className={styles.scrollLine} />
        </button>
        <HikerCharacter heroRef={heroRef} />
      </section>

      {/* ─── TRIPS ─── */}
      <section className={styles.section} id="trips" ref={tripsRef}>
        <div className={`${styles.sectionInner} ${tripsVisible ? styles.visible : ''}`}>
          <div className={styles.sectionTop}>
            <div>
              <span className={styles.sectionLabel}>This Week</span>
              <h2 className={styles.sectionTitle}>This Week's Trips</h2>
              <p className={styles.sectionSub}>Handpicked trails happening this week across Jordan's most spectacular landscapes.</p>
            </div>
            <button className={styles.viewAllBtn} onClick={() => navigate('/trips')}>
              All Trips <IconArrow size={16} />
            </button>
          </div>
          <div className={styles.tripsGrid}>
            {thisWeekTrips.map((trip, i) => (
              <div key={trip.id} className={styles.tripCardWrap} style={{ animationDelay: `${i * 0.08}s` }}>
                <TripCard trip={trip} />
              </div>
            ))}
          </div>
          {/* {thisWeekTrips.length > 0 && (
            <div className={styles.carouselHint}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="9 18 15 12 9 6" />
              </svg>
              Swipe to explore
            </div>
          )} */}
          {thisWeekTrips.length === 0 && (
            <div className={styles.emptyState}>
              <div className={styles.emptyIcon}>🌿</div>
              <p>No trips scheduled for this week yet. <button className={styles.inlineLink} onClick={() => navigate('/trips')}>Browse all trips →</button></p>
            </div>
          )}

          {/* ─── INLINE WEATHER ─── */}
          <div className={styles.inlineWeather}>
            <WeatherWidget />
          </div>
        </div>
      </section>

      {/* ─── ABOUT ─── */}
      <section className={`${styles.section} ${styles.aboutSection}`} id="about" ref={aboutRef}>
        <div className={`${styles.sectionInner} ${styles.aboutGrid} ${aboutVisible ? styles.visible : ''}`}>
          <div className={styles.aboutText}>
            <span className={styles.sectionLabel} style={{ color: 'rgba(232,99,26,0.9)' }}>Who We Are</span>
            <h2 className={styles.sectionTitle} style={{ color: 'white' }}>{data.about.title}</h2>
            <p className={styles.sectionSub} style={{ color: 'rgba(255,255,255,0.7)' }}>{data.about.body}</p>
            <div className={styles.aboutStats}>
              {data.about.stats.map((s, i) => (
                <div key={i} className={styles.statBox}>
                  <div className={styles.statValue}>{s.value}</div>
                  <div className={styles.statLabel}>{s.label}</div>
                </div>
              ))}
            </div>
            <a
              href={data.contact.instagramUrl}
              target="_blank"
              rel="noreferrer"
              className={styles.instagramLink}
            >
              <IconInstagram size={18} /> Follow us on Instagram
            </a>
          </div>
          <div className={styles.aboutImageSide}>
            <img
              src="https://images.unsplash.com/photo-1551632811-561732d1e306?w=900&q=80"
              alt="Hiking in Jordan"
              className={styles.aboutImg}
            />
            <div className={styles.aboutAccent}>
              <div className={styles.accentNum}>{data.about.stats.find(s => s.label === 'Instagram Followers')?.value || '33K'}</div>
              <div className={styles.accentText}>Instagram Followers</div>
            </div>
            <div className={styles.aboutFloat}>
              <IconTree size={20} />
              <span>Weekly hikes<br />every day</span>
            </div>
          </div>
        </div>
      </section>

      {/* ─── CORPORATE ─── */}
      <section className={`${styles.section} ${styles.corpSection}`} id="corporate" ref={corpRef}>
        <div className={`${styles.sectionInner} ${corpVisible ? styles.visible : ''}`}>
          <span className={styles.sectionLabel}>For Companies</span>
          <h2 className={styles.sectionTitle}>Corporate Programs</h2>
          <p className={styles.sectionSub}>Tailored outdoor experiences that strengthen teams and spark new energy.</p>
          <div className={styles.corpGrid}>
            {(data.corporate?.services || []).map((s, i) => (
              <div key={s.id} className={styles.corpCard}>
                <span className={styles.corpNum}>{String(i + 1).padStart(2, '0')}</span>
                <h3 className={styles.corpCardTitle}>{s.title}</h3>
                <p className={styles.corpCardBody}>{s.body}</p>
              </div>
            ))}
          </div>
          <button className={styles.corpCta} onClick={() => navigate('/corporate')}>
            Explore Corporate Page
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="9 18 15 12 9 6"/>
            </svg>
          </button>
        </div>
      </section>

      {/* ─── CONTACT ─── */}
      <section className={styles.section} id="contact" ref={contactRef}>
        <div className={`${styles.sectionInner} ${styles.contactGrid} ${contactVisible ? styles.visible : ''}`}>
          <div className={styles.contactForm}>
            <span className={styles.sectionLabel}>Get In Touch</span>
            <h2 className={styles.sectionTitle}>Contact us</h2>
            <p className={styles.sectionSub} style={{ marginBottom: 32 }}>
              Ready to explore Jordan's wild landscapes? Send us a message and we'll be in touch.
            </p>
            {contactState.succeeded ? (
              <div className={styles.successMsg}>
                ✅ Your message was sent successfully! We'll get back to you soon.
              </div>
            ) : (
              <form onSubmit={handleContactSubmit} className={styles.form}>
                <input type="hidden" name="_subject" value="New Contact Message – Keepers Experience" />
                <div className={styles.formGroup}>
                  <label className={styles.formLabel}>Full Name *</label>
                  <input
                    required className={styles.formInput}
                    placeholder="Your full name"
                    name="Full Name"
                  />
                  <ValidationError field="Full Name" errors={contactState.errors} />
                </div>
                <div className={styles.formRow}>
                  <div className={styles.formGroup}>
                    <label className={styles.formLabel}>Email</label>
                    <input
                      type="email" className={styles.formInput}
                      placeholder="your@email.com"
                      name="Email"
                    />
                    <ValidationError field="Email" errors={contactState.errors} />
                  </div>
                  <div className={styles.formGroup}>
                    <label className={styles.formLabel}>Phone *</label>
                    <input
                      required className={styles.formInput}
                      placeholder="+962 7x xxx xxxx"
                      name="Phone Number"
                    />
                    <ValidationError field="Phone Number" errors={contactState.errors} />
                  </div>
                </div>
                <div className={styles.formGroup}>
                  <label className={styles.formLabel}>Message</label>
                  <textarea
                    className={styles.formTextarea}
                    placeholder="Tell us more about what you're looking for..."
                    name="Message"
                  />
                  <ValidationError field="Message" errors={contactState.errors} />
                </div>
                <button type="submit" className={styles.btnPrimary} disabled={contactState.submitting}>
                  Send Message <IconArrow size={18} />
                </button>
              </form>
            )}
          </div>
          <div className={styles.contactInfo}>
            <h3 className={styles.contactInfoTitle}>Find Us</h3>
            <div className={styles.contactDetail}>
              <div className={styles.contactIcon}>📍</div>
              <div>
                <strong>Location</strong><br />
                Amman, Jordan<br />
                <span style={{ color: 'var(--text-light)', fontSize: 13 }}>Serving all of Jordan</span>
              </div>
            </div>
            <div className={styles.contactDetail}>
              <div className={styles.contactIcon}><IconWhatsapp size={18} /></div>
              <div>
                <strong>WhatsApp</strong><br />
                {data.contact.phone}
              </div>
            </div>
            <div className={styles.contactDetail}>
              <div className={styles.contactIcon}><IconInstagram size={18} /></div>
              <div>
                <strong>Instagram</strong><br />
                <a href={data.contact.instagramUrl} target="_blank" rel="noreferrer" className={styles.igLink}>
                  {data.contact.instagram}
                </a>
                <br />
                <span style={{ fontSize: 12, color: 'var(--text-light)' }}>{data.about.stats.find(s => s.label === 'Instagram Followers')?.value || '33K'} Followers · 1,130+ Posts</span>
              </div>
            </div>
            <div className={styles.tripHighlight}>
              <div className={styles.tripHighlightLabel}>Weekly trips every</div>
              <div className={styles.tripHighlightDays}>Every Day of the Week</div>
              <div className={styles.tripHighlightSub}>Private Trips · Corporate Programs · Experiences</div>
            </div>
          </div>
        </div>
      </section>

      {/* ─── BLOG ─── */}
      <section className={styles.blogSection} id="blog" ref={blogRef}>
        <div className={`${styles.sectionInner} ${blogVisible ? styles.visible : ''}`}>
          <div className={styles.sectionTop}>
            <div>
              <span className={styles.sectionLabel}>From the Trail</span>
              <h2 className={styles.sectionTitle}>Latest from the Blog</h2>
              <p className={styles.sectionSub}>Trip reports, hiking tips, and news from Jordan's outdoor community.</p>
            </div>
            <button className={styles.viewAllBtn} onClick={() => navigate('/blog')}>
              All Posts <IconArrow size={16} />
            </button>
          </div>
        </div>

        {/* ─── Marquee ─── */}
        {(() => {
          const catColors = {
            'trip-report': { color: '#2d7a3e', bg: '#e6f4ea' },
            'tips':        { color: '#1a56c4', bg: '#e8f0fe' },
            'news':        { color: '#b45309', bg: '#fef3c7' },
          };
          const catLabels = { 'trip-report': 'Trip Report', 'tips': 'Tips', 'news': 'News' };
          const posts = (data.posts || []).filter(p => p.published).slice().reverse();
          if (!posts.length) return null;
          // Duplicate enough times so marquee always fills the screen
          const repeated = [...posts, ...posts, ...posts, ...posts];
          return (
            <div className={styles.blogMarqueeWrap}>
              <div className={styles.blogTrack}>
                {repeated.map((post, i) => {
                  const cat = catColors[post.category] || { color: '#555', bg: '#f0f0f0' };
                  return (
                    <article
                      key={`${post.id}-${i}`}
                      className={styles.blogCard}
                      onClick={() => navigate(`/blog/${post.slug}`)}
                    >
                      <div className={styles.blogCardHeader} style={{ '--cat-color': cat.color, '--cat-bg': cat.bg }}>
                        <span className={styles.blogCatBadge} style={{ color: cat.color }}>
                          {catLabels[post.category] || post.category}
                        </span>
                      </div>
                      <div className={styles.blogCardBody}>
                        <h3 className={styles.blogCardTitle}>{post.title}</h3>
                        <p className={styles.blogCardExcerpt}>{post.excerpt}</p>
                      </div>
                      <div className={styles.blogCardFooter}>
                        <div className={styles.blogCardFooterLeft}>
                          <span className={styles.blogCardDate}>{post.date}</span>
                          <span className={styles.blogReadMore}>Read more →</span>
                        </div>
                        {post.coverImage && (
                          <div className={styles.blogCardThumb}>
                            <img src={post.coverImage} alt={post.title} loading="lazy" />
                          </div>
                        )}
                      </div>
                    </article>
                  );
                })}
              </div>
            </div>
          );
        })()}
      </section>

      {/* ─── GOOGLE REVIEWS ─── */}
      {(() => {
        const reviews = [
          {
            name: 'Nooradnan Abuzaid',
            meta: '2 reviews • 0 photos',
            time: '9 hours ago',
            text: 'حريصين علينا كتير أحلى الرحل بطلعها معهم مرتبين و منظمين كريمين friendly and funny\nThank you keepers',
            initial: 'N',
            avatarBg: '#1a7f5a',
          },
          {
            name: 'ameera Ismail',
            meta: '4 reviews • 7 photos',
            time: 'Yesterday',
            text: "I had such a great time 🕺🏻 it was a completely new experience for me. the Keepers team made everything smooth and enjoyable I honestly didn't expect to love it this much, but now I'm already planning to join them again",
            initial: 'A',
            avatarBg: '#c2185b',
          },
          {
            name: 'Manar Qaisi',
            meta: '2 reviews • 1 photo',
            time: '2 days ago',
            text: 'Very lovely and Safe Community ❤️',
            initial: 'M',
            avatarBg: '#00838f',
          },
          {
            name: 'Shahd Mahmoud',
            meta: '4 reviews • 6 photos',
            time: '2 days ago',
            text: "I had such a great experience with Keepers❤️\nThey were super organized and really cared about every detail.\nThey kept checking on us to make sure we were okay or needed anything\nHonestly one of the best groups I've ever dealt with!",
            initial: 'S',
            avatarBg: '#7b1fa2',
          },
          {
            name: 'Jehan',
            meta: '1 review • 0 photos',
            time: '3 days ago',
            text: 'Keepers team; every experience with them was genuine, and they followed the schedules they provided. The team respects everyone, is positive, cooperative, and all time asks if we need anything. The first experience was a test, and the ones after that were a choice based on the trust.',
            initial: 'J',
            avatarBg: '#e65100',
          },
        ];
        return (
          <section
            className={styles.reviewsSection}
            ref={reviewsRef}
          >
            <div className={`${styles.sectionInner} ${reviewsVisible ? styles.visible : ''}`}>
              {/* Section Title */}
              <div className={styles.reviewsSectionTop}>
                <h2 className={styles.reviewsSectionTitle}>What our clients say</h2>
              </div>

              {/* Rating Summary */}
              <div className={styles.ratingSummary}>
                <span className={styles.ratingNumber}>4.9</span>
                <div className={styles.ratingStars}>
                  {[...Array(5)].map((_, i) => (
                    <svg key={i} className={styles.starIcon} viewBox="0 0 24 24" fill="#FBBC04" xmlns="http://www.w3.org/2000/svg">
                      <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
                    </svg>
                  ))}
                </div>
                <span className={styles.ratingCount}>with more than 1K reviews</span>
              </div>

              {/* Review Cards - Marquee */}
              <div className={styles.reviewsMarqueeWrap}>
                <div className={styles.reviewsTrack}>
                  {[...reviews, ...reviews].map((r, i) => (
                  <div key={i} className={styles.reviewCard}>
                    <div className={styles.reviewCardTop}>
                      <div className={styles.reviewAuthor}>
                        <div className={styles.reviewAvatar} style={{ background: r.avatarBg }}>
                          {r.initial}
                        </div>
                        <div className={styles.reviewAuthorInfo}>
                          <span className={styles.reviewName}>{r.name}</span>
                          <span className={styles.reviewMeta}>{r.meta}</span>
                        </div>
                      </div>
                      {/* Google G */}
                      <svg className={styles.googleIcon} viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                        <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
                        <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                        <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z" fill="#FBBC05"/>
                        <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
                      </svg>
                    </div>
                    <div className={styles.reviewRatingRow}>
                      {[...Array(5)].map((_, si) => (
                        <svg key={si} className={styles.reviewStar} viewBox="0 0 24 24" fill="#FBBC04" xmlns="http://www.w3.org/2000/svg">
                          <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
                        </svg>
                      ))}
                      <svg className={styles.verifiedIcon} viewBox="0 0 24 24" fill="#4285F4" xmlns="http://www.w3.org/2000/svg">
                        <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" stroke="#4285F4" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                      <span className={styles.reviewTime}>{r.time}</span>
                    </div>
                    <p className={styles.reviewText}>{r.text}</p>
                  </div>
                  ))}
                </div>
              </div>

              {/* See more link */}
              <div className={styles.reviewsSeeMore}>
                <a
                  href="https://www.google.com/maps/place/Keepers+Experience/@31.2794439,37.122627,7z/data=!4m8!3m7!1s0x9b266501075073:0x6e9461ac642a342f!8m2!3d31.279444!4d37.122627!9m1!1b1!16s%2Fg%2F11wg6w6x7l?entry=ttu&g_ep=EgoyMDI2MDQwOC4wIKXMDSoASAFQAw%3D%3D"
                  target="_blank"
                  rel="noopener noreferrer"
                  className={styles.reviewsSeeMoreLink}
                >
                  See all reviews on Google
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={styles.reviewsSeeMoreArrow}>
                    <path d="M5 12h14M12 5l7 7-7 7"/>
                  </svg>
                </a>
              </div>
            </div>
          </section>
        );
      })()}

      {/* ─── FOOTER ─── */}
      <Footer />
    </div>
    </>
  );
}
